import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { NotFoundGuard } from './guards/not-found.guard';
import { getFullPath } from './lib/get-full-path';
import { AffiliateService } from './services/affiliate.service';
import { RedirectService } from './services/redirect.service';
import { StoryblokService } from './services/storyblok.service';
import { storyblokStoryResolver } from './storyblok/storyblok-story.resolver';

const isInternalLink = (url: string) => {
  if (!url) return false;
  return url.startsWith('/');
};

const redirect = (url: string, redirectService: RedirectService) => {
  if (isInternalLink(url)) {
    const path = url
      .split('?')[0]
      .split('#')[0]
      .split('/')
      .filter(a => a);
    const searchString = url.split('?')[1]?.split('#')[0];
    const searchParams = new URLSearchParams(searchString);
    const query = {};
    for (const [key, value] of searchParams.entries()) {
      query[key] = value;
    }
    return redirectService.redirectTo(path, query);
  }

  return redirectService.externalRedirectTo(url);
};

export const storyblokStoryGuard: CanActivateFn = async route => {
  const storyblokService = inject(StoryblokService);
  const redirectService = inject(RedirectService);
  const affiliateService = inject(AffiliateService);
  const notFoundGuard = inject(NotFoundGuard);
  const story = await lastValueFrom(storyblokStoryResolver(route));
  if (!story) {
    const path = getFullPath(route);
    if (path.length === 1 && path[0]) {
      const affiliate = await affiliateService.getAffiliate(path[0]);
      if (affiliate) return await affiliateService.affiliateRedirect(affiliate);
    }
    return notFoundGuard.canActivate();
  }
  if (story.content.component === 'Redirect') {
    return redirect(storyblokService.getUrlLink(story.content.redirectTo), redirectService);
  }

  if (story.content.affiliate) {
    const relationships = await lastValueFrom(storyblokService.resolveRelationships(route));
    const affiliate = relationships.find(r => r.uuid === story.content.affiliate);
    if (route.queryParams.ref !== affiliate.slug) {
      return redirectService.redirectTo(story.full_slug.split('/'), {
        ...route.queryParams,
        source: affiliate.content.source,
        medium: affiliate.content.medium,
        campaign: affiliate.content.campaign,
        ref: affiliate.slug,
      });
    }
  }

  if (route.queryParams.ref) {
    const affiliate = await affiliateService.getAffiliate(route.queryParams.ref);
    if (affiliate) {
      await affiliateService.handleAffiliate(affiliate);
    }
  }

  await storyblokService.loadComponents(story);

  return true;
};
